/**
 * Container Table Component
 *
 * Responsive table/card view for displaying containers.
 * Desktop: Full table layout
 * Mobile: Card-based layout for better touch interaction
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { Container } from '@/services/resource-container.service';
import {
  calculateDaysOnSite,
  formatDeploymentDate,
  getDaysOnSiteBadgeVariant,
} from '@/utils/formatting';

interface ContainerTableProps {
  containers: Container[];
  onAssign: (container: Container) => void;
  onRetrieve: (container: Container) => void;
  onReturnToYard: (container: Container) => void;
  onMaintenance: (container: Container) => void;
  isLoading?: boolean;
}

/**
 * Get status badge variant based on container status
 */
function getStatusVariant(
  status: Container['status']
): 'success' | 'default' | 'warning' | 'secondary' {
  switch (status) {
    case 'available':
      return 'success';
    case 'deployed':
      return 'default';
    case 'in_transit':
      return 'warning';
    case 'maintenance':
      return 'warning';
    case 'retired':
      return 'secondary';
    default:
      return 'secondary';
  }
}

/**
 * Get condition badge variant
 */
function getConditionVariant(
  condition: Container['condition']
): 'success' | 'warning' | 'destructive' | 'secondary' {
  switch (condition) {
    case 'excellent':
    case 'good':
      return 'success';
    case 'fair':
      return 'warning';
    case 'poor':
    case 'needs_repair':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Desktop table row component
 */
function ContainerRow({
  container,
  onAssign,
  onRetrieve,
  onReturnToYard,
  onMaintenance,
}: {
  container: Container;
  onAssign: () => void;
  onRetrieve: () => void;
  onReturnToYard: () => void;
  onMaintenance: () => void;
}) {
  const canAssign = container.status === 'available';
  const canRetrieve = container.status === 'deployed';
  const canReturnToYard = container.location.type !== 'yard';

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        {container.containerNumber}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">{container.size}</td>
      <td className="px-4 py-3 text-sm text-gray-700 capitalize">{container.type}</td>
      <td className="px-4 py-3">
        <Badge variant={getStatusVariant(container.status)}>
          {container.status.replace('_', ' ')}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <Badge variant={getConditionVariant(container.condition)}>
          {container.condition.replace('_', ' ')}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        <div className="max-w-[200px] truncate" title={container.location.address}>
          {container.location.type === 'yard' ? 'Yard' : container.location.address}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {container.assignment ? (
          <div className="max-w-[150px] truncate" title={container.assignment.jobAddress}>
            Job Site
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {container.assignment ? (
          <Badge variant={getDaysOnSiteBadgeVariant(calculateDaysOnSite(container.assignment.deploymentDate))}>
            {calculateDaysOnSite(container.assignment.deploymentDate)} days
          </Badge>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {canAssign && (
            <Button size="sm" variant="outline" onClick={onAssign}>
              Assign
            </Button>
          )}
          {canRetrieve && (
            <Button size="sm" variant="outline" onClick={onRetrieve}>
              Retrieve
            </Button>
          )}
          {canReturnToYard && (
            <Button size="sm" variant="outline" onClick={onReturnToYard}>
              Return to Yard
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onMaintenance}>
            Maintenance
          </Button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Mobile card component
 */
function ContainerCard({
  container,
  onAssign,
  onRetrieve,
  onReturnToYard,
  onMaintenance,
}: {
  container: Container;
  onAssign: () => void;
  onRetrieve: () => void;
  onReturnToYard: () => void;
  onMaintenance: () => void;
}) {
  const canAssign = container.status === 'available';
  const canRetrieve = container.status === 'deployed';
  const canReturnToYard = container.location.type !== 'yard';

  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-lg text-gray-900">
              {container.containerNumber}
            </h3>
            <p className="text-sm text-gray-600">
              {container.size} - {container.type}
            </p>
          </div>
          <Badge variant={getStatusVariant(container.status)}>
            {container.status.replace('_', ' ')}
          </Badge>
        </div>

        {/* Details Grid */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Condition:</span>
            <Badge variant={getConditionVariant(container.condition)}>
              {container.condition.replace('_', ' ')}
            </Badge>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Location:</span>
            <span className="text-gray-900 font-medium">
              {container.location.type === 'yard'
                ? 'Yard'
                : container.location.type.replace('_', ' ')}
            </span>
          </div>

          {container.assignment && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Assignment:</span>
                <span className="text-gray-900 font-medium">Job Site</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Deployed:</span>
                <span className="text-gray-900 font-medium">
                  {formatDeploymentDate(container.assignment.deploymentDate)}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Days on Site:</span>
                <Badge variant={getDaysOnSiteBadgeVariant(calculateDaysOnSite(container.assignment.deploymentDate))}>
                  {calculateDaysOnSite(container.assignment.deploymentDate)} days
                </Badge>
              </div>
            </>
          )}

          <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
            {container.location.address}
          </div>
        </div>

        {/* Actions - Mobile optimized with larger touch targets */}
        <div className="flex flex-col gap-2">
          {canAssign && (
            <Button
              size="lg"
              variant="default"
              onClick={onAssign}
              className="w-full min-h-[44px]"
            >
              Assign to Job
            </Button>
          )}
          {canRetrieve && (
            <Button
              size="lg"
              variant="default"
              onClick={onRetrieve}
              className="w-full min-h-[44px]"
            >
              Retrieve from Job
            </Button>
          )}
          {canReturnToYard && (
            <Button
              size="lg"
              variant="default"
              onClick={onReturnToYard}
              className="w-full min-h-[44px]"
            >
              Return to Yard
            </Button>
          )}
          <Button
            size="lg"
            variant="outline"
            onClick={onMaintenance}
            className="w-full min-h-[44px]"
          >
            Mark for Maintenance
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Main ContainerTable component with responsive behavior
 */
export function ContainerTable({
  containers,
  onAssign,
  onRetrieve,
  onReturnToYard,
  onMaintenance,
  isLoading,
}: ContainerTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-sm text-gray-600">Loading containers...</p>
        </div>
      </div>
    );
  }

  if (containers.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No containers found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by creating a new container.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View - Hidden on mobile */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Container #
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Size
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Condition
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Location
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Assignment
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Days on Site
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {containers.map((container) => (
              <ContainerRow
                key={container.id}
                container={container}
                onAssign={() => onAssign(container)}
                onRetrieve={() => onRetrieve(container)}
                onReturnToYard={() => onReturnToYard(container)}
                onMaintenance={() => onMaintenance(container)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Hidden on desktop */}
      <div className="md:hidden">
        {containers.map((container) => (
          <ContainerCard
            key={container.id}
            container={container}
            onAssign={() => onAssign(container)}
            onRetrieve={() => onRetrieve(container)}
            onReturnToYard={() => onReturnToYard(container)}
            onMaintenance={() => onMaintenance(container)}
          />
        ))}
      </div>
    </>
  );
}
